import React, { useEffect, useState } from "react";
import { Trash2, Plus, Minus, Search} from 'lucide-react';
import { Link } from "react-router-dom";
import { CartProductView } from "Common/Models/CartProductView";
import withMessages, { WithMessagesProps } from "Common/withMessages";
import withNotifications, { WithNotificationsProps } from "Common/withNotifications";
import { NotificationType } from "Common/Components/Notifications/notificationTypes";
import { WithAuthProps } from "Common/Components/Authentication/WithAuthProps";
import withAuth from "Common/Components/Authentication/withAuth";
import { axios_helper_async } from "helpers/Axios";
import { MessageType } from "Common/Components/Messages/messageTypes";
import { WithCartProps } from "Common/Components/Cart/WithCartProps";
import { withCart } from "Common/Components/Cart/withCart";
import { CartProduct } from "Common/Models/CartProduct";
import { WithTranslationProps } from "Common/Components/Translation/WithTranslationProps";
import withTranslation from "Common/withTranslation";
import { withCurrency } from "Common/Components/Currency/withCurrency";
import { WithCurrencyProps } from "Common/Components/Currency/WithCurrencyProps";
import Popup from "Common/Components/Popup/Popup";

const ShoppingCart: React.FC<WithMessagesProps & WithNotificationsProps & WithAuthProps & WithCartProps & WithTranslationProps & WithCurrencyProps> = ({
    messages,
    notifications,
    account,
    cart,
    t,
    currency,
    c
}) => {
    const [products, setProducts] = useState<CartProductView[]>([]);
    const [subtotal, setSubtotal] = useState<number>(0);
    // const [shippingCharge, setShippingCharge] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [tax, setTax] = useState<number>(0);
    const [quantityVerification, setQuantityVerification] = useState<boolean>(false);

    const deleteToggle = async (id: number) => {
        if (await messages.showMessage("Cart.Delete Item Confirmation", MessageType.Question, "Cart.Delete Item")) {
            await removeCartItem(id);
        }
    };

    const removeCartItem = async (id: number) => {
        
        if(await deleteCartProduct(id)) {
            const filtered = products.filter((item: CartProductView) => {
                return item.productId !== id;
            });
            setProducts(filtered);
        }

    };

    const removeCartItemAll = async () => {
        if (await messages.showMessage("Cart.Delete Items Confirmation", MessageType.Question, "Cart.Delete All")) {
            if(await deleteCartProducts()) {
                setProducts([]);
            }
        }
    };

    const countUP = async (id: number) => {
        setQuantityVerification(true);
        const max = await getProductCount(id);
        const oldProduct = products.find(x => x.productId === id);
        if (max >= 0 && oldProduct) {
            const newValue = oldProduct.quantity + 1;
            const newProdcut = { ...oldProduct, quantity: newValue > max ? max : newValue }
            updateCartProductItem(id, newProdcut);            
        }
        setQuantityVerification(false)
    };

    const countDown = async (id: number) => {
        setQuantityVerification(true);
        const max = await getProductCount(id);
        const oldProduct = products.find(x => x.productId === id);
        if (max >= 0 && oldProduct) {
            const newValue = oldProduct.quantity - 1
            const newProdcut = { ...oldProduct, quantity: newValue < 0 ? 0 : (newValue > max ? max : newValue) }
            updateCartProductItem(id, newProdcut);
        }
        setQuantityVerification(false)
    };

    const updateCartProductItem = async (id: number, newProdcut: CartProductView) => {
        if (await updateCartProductCount(newProdcut.productId, newProdcut.quantity)) {
            setProducts((prevModel) =>
                prevModel.map((product) =>
                    product.productId === id ? newProdcut : product
                )
            );
        }
    }

    const getProducts = async () => {
        try {
            setLoading(true);
            const body = {
                customerId: account.id
            }

            const response = await axios_helper_async<CartProductView[]>("/Cart/Get-Cart-Products", 'GET', body);

            messages.setMessages(response.messages);
            notifications.setNotifications(response.notifications);

            if (response.isSuccess && response.data) {
                setProducts(response.data);
            }
        }
        catch (ex: any) {
            notifications.showNotification(`${ex}`, NotificationType.Error);
        }
        finally {
            setLoading(false);
        }
    }

    const getProductCount = async (id: number): Promise<number> => {
        var res = 0;
        try {
            setLoading(true);
            const body = {
                productId: id
            }

            const response = await axios_helper_async<number>("/Cart/Get-Product-Count", 'GET', body);

            messages.setMessages(response.messages);
            notifications.setNotifications(response.notifications);

            if (response.isSuccess && response.data !== null) {
                res = response.data;
            }
            else {
                res = -1;
            }
        }
        catch (ex: any) {
            notifications.showNotification(`${ex}`, NotificationType.Error);
            res = -1;
        }
        finally {
            setLoading(false);
        }

        return res;
    }

    const updateCartProductCount = async (id: number, quantity: number): Promise<boolean> => {
        setQuantityVerification(true);
        var res = false;
        try {
            const body: CartProduct = {
                customerId: Number(account.id),
                productId: id,
                quantity: quantity
            }

            const response = await axios_helper_async<boolean>("/Cart/Update-Cart-Product", 'PATCH', body);

            messages.setMessages(response.messages);
            notifications.setNotifications(response.notifications);

            if (response.isSuccess && response.data !== null) {
                res = response.data;
            }
        }
        catch (ex: any) {
            notifications.showNotification(`${ex}`, NotificationType.Error);
        }
        finally {
            setQuantityVerification(false);
        }
        
        return res;
    }

    const deleteCartProduct = async (id?: number): Promise<boolean> => {
        setQuantityVerification(true);        
        var res = false;        
        try {
            if(id) {
                const body = {
                    customerId: Number(account.id),
                    productId: id,
                }
    
                const response = await axios_helper_async<boolean>("/Cart/Delete-Cart-Product", 'DELETE', body);
    
                messages.setMessages(response.messages);
                notifications.setNotifications(response.notifications);
    
                if (response.isSuccess && response.data !== null) {
                    res = response.data;
                }
            }
        }
        catch (ex: any) {
            notifications.showNotification(`${ex}`, NotificationType.Error);
        }
        finally {
            setQuantityVerification(false);
        }
        
        return res;
    }

    const deleteCartProducts = async (): Promise<boolean> => {
        setQuantityVerification(true);
        var res = false;
        try {
            const body = {
                customerId: Number(account.id),
            }

            const response = await axios_helper_async<boolean>("/Cart/Delete-Cart-Products", 'DELETE', body);

            messages.setMessages(response.messages);
            notifications.setNotifications(response.notifications);

            if (response.isSuccess && response.data !== null) {
                res = response.data;
            }
        }
        catch (ex: any) {
            notifications.showNotification(`${ex}`, NotificationType.Error);
        }
        finally {
            setQuantityVerification(false);
        }
        
        return res;
    }

    useEffect(() => {
        var sum: number = 0;
        products.forEach(product => {
            sum += product.quantity * product.price;
        });

        setSubtotal(sum);

        (async () => {
            await cart.updateCart();
        })();
    }, [products])

    useEffect(() => {
        let tax = (0.16 * subtotal);

        // if (subtotal !== 0) {
        //     setShippingCharge(65);
        // } else {
        //     setShippingCharge(0);
        // }
        setTax(tax);
        // setTotal(subtotal + shippingCharge + tax);
        setTotal(subtotal + tax);
    }, [subtotal]);    

    useEffect(() => {
        (async () => {
            await getProducts();
        })()
    }, []);

    return (
        <React.Fragment>
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-x-5">
                <div className="xl:col-span-9 products-list">
                    <div className="flex justify-end items-center gap-3 mb-5">
                        <div>
                            <Link to="#!" className="text-red-500 transition-all duration-300 ease-linear hover:text-red-600" onClick={removeCartItemAll}><Trash2 className="inline-block w-4 h-4 mr-1 align-middle" /> <span className="align-middle">{t('Cart.Delete All')}</span></Link>
                        </div>
                    </div>

                    {(products).map((item, key) => (<div className="card products" id={`product${item.name}`} key={key}>
                        <div className="card-body">
                            <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
                                <div className="p-4 rounded-md lg:col-span-2 bg-slate-100 dark:bg-zink-600">
                                    <img src={item.image} alt="" />
                                </div>
                                <div className="flex flex-col lg:col-span-4">
                                    <div>
                                        <h5 className="mb-1 text-16"><Link to={`/Product?category=${item.category}&product=${item.productId}`} target="_blank" rel="noreferrer">{item.name}</Link></h5>
                                        <p className="mb-2 text-slate-500 dark:text-zink-200">{item.description}</p>
                                        <p className="mb-1 text-slate-500 dark:text-zink-200">{t('Cart.Brand')}: <span className="text-slate-800 dark:text-zink-50">{item.brand}</span></p>
                                    </div>
                                    <div className="flex items-center gap-2 mt-auto">
                                        <div className="inline-flex p-2 text-center border rounded input-step border-slate-200 dark:border-zink-500">
                                            <button type="button" className="border w-7 leading-[15px] minus-value bg-slate-200 dark:bg-zink-600 dark:border-zink-600 rounded transition-all duration-200 ease-linear border-slate-200 text-slate-500 dark:text-zink-200 hover:bg-custom-500 dark:hover:bg-custom-500 hover:text-custom-50 dark:hover:text-custom-50 hover:border-custom-500 dark:hover:border-custom-500 focus:bg-custom-500 dark:focus:bg-custom-500 focus:border-custom-500 dark:focus:border-custom-500 focus:text-custom-50 dark:focus:text-custom-50"
                                                onClick={() => countDown(item.productId)} disabled={quantityVerification}>
                                                <Minus className="inline-block w-4 h-4" /></button>
                                            <input type="number" className="text-center ltr:pl-2 rtl:pr-2 w-15 h-7 products-quantity dark:bg-zink-700 focus:shadow-none" value={item.quantity} min="0" max="100" readOnly />
                                            <button type="button" className="transition-all duration-200 ease-linear border rounded border-slate-200 bg-slate-200 dark:bg-zink-600 dark:border-zink-600 w-7 plus-value text-slate-500 dark:text-zink-200 hover:bg-custom-500 dark:hover:bg-custom-500 hover:text-custom-50 dark:hover:text-custom-50 hover:border-custom-500 dark:hover:border-custom-500 focus:bg-custom-500 dark:focus:bg-custom-500 focus:border-custom-500 dark:focus:border-custom-500 focus:text-custom-50 dark:focus:text-custom-50"
                                                onClick={() => countUP(item.productId)} disabled={quantityVerification}>
                                                <Plus className="inline-block w-4 h-4" /></button>
                                        </div>
                                        <button type="button" className="flex items-center justify-center w-[37.5px] h-[37.5px] p-0 text-red-500 bg-red-100 btn hover:text-white hover:bg-red-600 focus:text-white focus:bg-red-600 focus:ring focus:ring-red-100 active:text-white active:bg-red-600 active:ring active:ring-red-100 dark:bg-red-500/20 dark:text-red-500 dark:hover:bg-red-500 dark:hover:text-white dark:focus:bg-red-500 dark:focus:text-white dark:active:bg-red-500 dark:active:text-white dark:ring-red-400/20 remove-button" onClick={() => deleteToggle(item.productId)}><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                </div>
                                <div className="flex justify-between w-full lg:flex-col lg:col-end-13 lg:col-span-2">
                                    <div className="mb-auto ltr:lg:text-right rtl:lg:text-left">
                                        <h6 className="text-16 products-price">$<span>{c(item.price).toFixed(2)}</span></h6>
                                    </div>
                                    <h6 className="mt-auto text-16 ltr:lg:text-right rtl:lg:text-left">$<span className="products-line-price">{(c(item.price * item.quantity)).toFixed(2)}</span></h6>
                                </div>
                            </div>
                        </div>
                    </div>))}

                    {!products.length && <div className="noresult">
                        <div className="py-6 text-center">
                            <Search className="w-6 h-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" />
                            <h5 className="mt-2 mb-1">{t('Cart.Not Items Found')}</h5>
                            {/* <p className="mb-0 text-slate-500 dark:text-zink-200">We've searched more than 299+ orders We did not find any orders for you search.</p> */}
                        </div>
                    </div>}
                </div>
                <div className="xl:col-span-3">
                    <div className="sticky top-[calc(theme('spacing.header')_*_1.3)] mb-5">
                        <div className="card ">
                            <div className="card-body">
                                <h6 className="mb-4 text-15">{t('Cart.Order Summary')}</h6>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <tbody className="table-total">
                                            <tr>
                                                <td className="py-2 text-slate-500 dark:text-zink-200">
                                                    {t('Cart.Sub Total')}
                                                </td>
                                                <td className="py-2 text-slate-500 dark:text-zink-200 cart-subtotal">
                                                    ${c(subtotal).toFixed(2)}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 text-slate-500 dark:text-zink-200">
                                                    {t('Cart.Estimated Tax')} (16%)
                                                </td>
                                                <td className="py-2 text-slate-500 dark:text-zink-200 cart-tax">
                                                    ${c(tax).toFixed(2)}
                                                </td>
                                            </tr>
                                            {/* <tr>
                                                <td className="py-2 text-slate-500 dark:text-zink-200">
                                                    {t('Cart.Shipping Charge')}
                                                </td>
                                                <td className="py-2 text-slate-500 dark:text-zink-200 cart-shipping">
                                                    ${shippingCharge}
                                                </td>
                                            </tr> */}
                                            <tr className="font-semibold">
                                                <td className="pt-2">
                                                    <div className="flex flex-row justify-start items-center gap-2 w-full">
                                                        <>
                                                        {t('Cart.Total Amount')} ({currency.value}) 
                                                        </>
                                                        <Popup text="Cart.Warning Message" />
                                                    </div>
                                                </td>
                                                <td className="pt-2 cart-total">
                                                    ${c(total).toFixed(2)}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-5 shrink-0">
                            <Link to="/categories" className="w-full text-white bg-red-500 border-red-500 btn hover:text-white hover:bg-red-600 hover:border-red-600 focus:text-white focus:bg-red-600 focus:border-red-600 focus:ring focus:ring-red-100 active:text-white active:bg-red-600 active:border-red-600 active:ring active:ring-red-100 dark:ring-red-400/20">{t('Cart.Continue to Shopping')}</Link>
                            {products.length > 0 && <Link to="/checkout" className="w-full text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20">{t('Cart.Checkout')}</Link>}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default withMessages(withNotifications(withAuth(withCart(withTranslation(withCurrency(ShoppingCart))))));